import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { normalizeUtcString } from '../../../../utils/timezoneFormatter';
import { normalizeTimezone } from '../../../../utils/timezoneUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 审核池时间统一按 **hub 所在时区** 呈现与筛选。
 *
 * 背景：审核池是 hub 级工作台，卡片时间、「今日新增」、日期区间筛选说的都是「站点当地那一天」。
 * 原实现清一色 `new Date(iso).getHours()/getDate()`，取的是**浏览器本地**时区 ——
 * 运营在北京看 LAX 的 hub 会差 15 小时：卡片时间对不上作业时间，「今日新增」和日期筛选
 * 跨天错位（北京上午 = LAX 前一天下午）。
 *
 * 时区取值优先级：
 * 1. `timezoneLocation`（IANA，如 America/Los_Angeles）—— 能正确处理夏令时，首选；
 * 2. `timezone`（形如 `UTC-7` 的固定偏移）—— LAX 冬令时会差 1 小时，仅兜底；
 * 3. 都取不到 → 退回浏览器本地，至少不崩、不整体报错。
 */
export function useHubZone(): string | undefined {
  const { currentWarehouse } = useWarehouseContext();
  const w = currentWarehouse as { timezoneLocation?: string; timezone?: string } | undefined;
  return w?.timezoneLocation || w?.timezone || undefined;
}

/**
 * 把后端时间字符串换算到 hub 时区。
 *
 * 后端 `createdAt` 是无时区后缀的 `YYYY-MM-DD HH:mm:ss`（UTC 值），直接 `dayjs(s)` /
 * `new Date(s)` 会被按浏览器本地时区解析，凭空多一层偏移，所以先过 `normalizeUtcString` 补 Z。
 */
export function hubDayjs(iso: string | null | undefined, hubZone?: string): Dayjs | null {
  if (!iso) return null;
  const m = dayjs.utc(normalizeUtcString(iso));
  if (!m.isValid()) return null;
  if (!hubZone) return m.local();
  return hubZone.includes('/') ? m.tz(hubZone) : m.utcOffset(normalizeTimezone(hubZone));
}

/** 卡片短格式 MM-DD HH:mm（对齐 PRD「07-06 08:10」）；解析不出来时原样回显，不吞信息 */
export function formatHubTimeShort(iso: string | null | undefined, hubZone?: string): string {
  if (!iso) return '';
  return hubDayjs(iso, hubZone)?.format('MM-DD HH:mm') ?? iso;
}

/**
 * 跟进记录 / 建联时间线的完整格式 YYYY-MM-DD HH:mm:ss；解析不出来时原样回显。
 *
 * 与 {@link formatHubTimeShort} 的区别只有格式串 —— 时间线要能分辨"首联和上报差了几秒"，
 * MM-DD HH:mm 精度不够（2.2「跟进记录显示真实操作时间」正是靠秒级差异体现出来的）。
 *
 * <p><b>为什么必须走 hubDayjs 而不是 new Date(iso)</b>：服务端所有时间都是
 * `LocalDateTime.toString()` 产出的**无时区后缀**串（容器 `ENV TZ=UTC` + 数据源
 * `serverTimezone=UTC` ⇒ 值是 UTC 墙上时间）。`new Date(无后缀串)` 按浏览器本地解析、
 * `getHours()` 又原样取回，净效果是把 UTC 直接当本地打印 —— 运营在北京看到的是差 8 小时的
 * 时间，看 LAX 的 hub 更是差 15 小时。审核池列表侧一直是对的（走 hubDayjs 补 Z 再转），
 * 只有跟进记录这一族用了裸 new Date，本次统一。
 */
export function formatHubTimeFull(iso: string | null | undefined, hubZone?: string): string {
  if (!iso) return '';
  return hubDayjs(iso, hubZone)?.format('YYYY-MM-DD HH:mm:ss') ?? iso;
}

/** hub 时区下的日期键 YYYY-MM-DD，用于「今日新增」「日期区间」按 hub 当地天数比较 */
export function hubDateKey(iso: string | null | undefined, hubZone?: string): string | null {
  return hubDayjs(iso, hubZone)?.format('YYYY-MM-DD') ?? null;
}

/** hub 时区下的「今天」YYYY-MM-DD */
export function hubToday(hubZone?: string): string {
  const now = dayjs.utc();
  if (!hubZone) return now.local().format('YYYY-MM-DD');
  return (hubZone.includes('/') ? now.tz(hubZone) : now.utcOffset(normalizeTimezone(hubZone))).format(
    'YYYY-MM-DD',
  );
}
